import React, { useMemo, useState } from 'react';
import { sortMasterDataValues } from '../../Utils/projectMasterDataUtils';
import MasterDataItemModal from '../forms/MasterDataItemModal';

const MasterDataFieldPanel = ({
  field,
  items,
  saving,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
}) => {
  const [newValue, setNewValue] = useState('');
  const [query, setQuery] = useState('');
  const [addError, setAddError] = useState('');
  const [editingValue, setEditingValue] = useState(null);

  const filteredItems = useMemo(() => {
    const queryValue = query.trim().toLowerCase();
    const sortedItems = sortMasterDataValues(items);
    if (!queryValue) {
      return sortedItems;
    }

    return sortedItems.filter((item) => item.toLowerCase().includes(queryValue));
  }, [items, query]);

  const handleAdd = async (event) => {
    event.preventDefault();
    const trimmedValue = newValue.trim();
    if (!trimmedValue) {
      setAddError('Enter a value before adding.');
      return;
    }

    setAddError('');

    try {
      await onAddItem(field.key, trimmedValue);
      setNewValue('');
    } catch (error) {
      setAddError(error?.message || 'Failed to add dropdown value.');
    }
  };

  const handleSaveEdit = async (updatedValue) => {
    if (!editingValue) {
      return;
    }

    await onUpdateItem(field.key, editingValue, updatedValue);
    setEditingValue(null);
  };

  return (
    <section className="pae-master-data-panel">
      <header className="pae-master-data-panel-header">
        <div>
          <h2>{field.label}</h2>
          <p>{field.description}</p>
        </div>
        <span className="pae-master-data-count">{items.length} items</span>
      </header>

      <div className="pae-master-data-toolbar">
        <label className="partner-companies-search partner-pae-master-data-search">
          <span className="partner-companies-search-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path
                d="M10.5 4a6.5 6.5 0 1 1 0 13 6.5 6.5 0 0 1 0-13zm0 2a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9zm7.03 11.47-2.77-2.77 1.41-1.41 2.77 2.77-1.41 1.41z"
                fill="currentColor"
              />
            </svg>
          </span>
          <input
            className="form-control partner-companies-search-input"
            type="text"
            placeholder={`Search ${field.label.toLowerCase()}...`}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label={`Search ${field.label}`}
          />
          {query.trim() ? (
            <button
              type="button"
              className="partner-companies-search-clear"
              onClick={() => setQuery('')}
              aria-label="Clear search"
            >
              <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                <path
                  d="M8.4 7 7 8.4 10.6 12 7 15.6 8.4 17 12 13.4 15.6 17 17 15.6 13.4 12 17 8.4 15.6 7 12 10.6 8.4 7z"
                  fill="currentColor"
                />
              </svg>
            </button>
          ) : null}
        </label>
      </div>

      <div className="pae-master-data-list-wrap">
        {filteredItems.length === 0 ? (
          <p className="pae-master-data-empty">
            {items.length === 0 ? 'No values configured yet.' : 'No values match your search.'}
          </p>
        ) : (
          <ul className="pae-master-data-list">
            {filteredItems.map((item) => (
              <li key={item} className="pae-master-data-list-item">
                <span className="pae-master-data-value" title={item}>{item}</span>
                <div className="pae-master-data-item-actions">
                  <button
                    type="button"
                    className="pae-master-data-edit-btn"
                    onClick={() => setEditingValue(item)}
                    disabled={saving}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="pae-master-data-delete-btn"
                    onClick={() => onDeleteItem(field.key, item)}
                    disabled={saving}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <form className="pae-master-data-add-form" onSubmit={handleAdd}>
        <div className="pae-master-data-add-row">
          <input
            className={`form-control pae-master-data-add-input ${addError ? 'is-invalid' : ''}`}
            placeholder={field.addPlaceholder}
            value={newValue}
            onChange={(event) => {
              setNewValue(event.target.value);
              setAddError('');
            }}
            disabled={saving}
          />
          <button type="submit" className="btn btn-primary pae-master-data-add-btn" disabled={saving || !newValue.trim()}>
            {saving ? 'Saving...' : 'Add New'}
          </button>
        </div>
      </form>
      {addError ? <p className="text-danger pae-master-data-add-error">{addError}</p> : null}

      <MasterDataItemModal
        open={Boolean(editingValue)}
        fieldLabel={field.label}
        currentValue={editingValue}
        saving={saving}
        onClose={() => setEditingValue(null)}
        onSave={handleSaveEdit}
      />
    </section>
  );
};

export default MasterDataFieldPanel;
